import { isEmpty, isUndefined, some } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import dynamic from 'next/dynamic';
import React, { useCallback, useRef, useState } from 'react';

import { Button } from '../button';
import { Flex } from '../grid';
import { Icon } from '../icons';
import { Label } from '../typography';
import { LoadingIndicator } from '../icons/loading-indicator';
import { StoredCard } from '../stored-card';

const CreditCardForm = dynamic(() => import('./credit-card-form').then(mod => mod.CreditCardForm), {
  ssr: false,
});

const CardComponent = () => {
  const { PaymentStore, CartStore, MemberStore } = useRootStore();
  const creditCardBrand = useRef(null);

  const { paymentMethodId } = CartStore.payment;

  const [payWithCreditCardSelected, setPayWithCreditCardSelected] = useState(!!paymentMethodId);
  const showCreditCardForm = payWithCreditCardSelected || isEmpty(PaymentStore.storedPaymentMethods);

  const onBrandChange = useCallback(brand => {
    creditCardBrand.current = brand.brand;
  }, []);

  const onCreditCardChange = useCallback(
    ({ data, isValid }) => {
      const { browserInfo, paymentMethod, riskData, storePaymentMethod } = data;
      if (isValid) {
        const {
          encryptedCardNumber,
          encryptedExpiryMonth,
          encryptedExpiryYear,
          encryptedSecurityCode,
          holderName,
        } = paymentMethod;

        if (
          some([encryptedCardNumber, encryptedExpiryMonth, encryptedExpiryYear, encryptedSecurityCode], isUndefined)
        ) {
          return;
        }

        const creditCard = {
          brand: creditCardBrand.current,
          browserInfo,
          encryptedCardNumber,
          encryptedExpiryMonth,
          encryptedExpiryYear,
          encryptedSecurityCode,
          id: 'credit-card',
          name: holderName,
          riskData,
        };

        CartStore.payment.setStorePaymentMethod(storePaymentMethod);
        CartStore.payment.setCreditCard(creditCard);
        CartStore.payment.setUseNewCreditCard(true);
      }
    },
    [CartStore]
  );

  return (
    <>
      {PaymentStore.loading ? (
        <LoadingIndicator />
      ) : (
        <>
          {!isEmpty(PaymentStore.storedPaymentMethods) && (
            <>
              <Label mt={1} mb="0.5rem" color="greyPrimary" fontWeight="normal">
                SAVED PAYMENT METHODS
              </Label>
              {PaymentStore.storedPaymentMethods.map(item => (
                <StoredCard
                  {...item}
                  key={item.id}
                  selected={item.id === paymentMethodId}
                  onClick={() => {
                    setPayWithCreditCardSelected(false);
                    CartStore.payment.setUseNewCreditCard(false);
                    CartStore.payment.setPaymentMethod(item.id);
                  }}
                />
              ))}
            </>
          )}
          {showCreditCardForm && (
            <Flex mt={1} alignItems="center" justifyContent="flex-start">
              <Label mt={1} mb="0.5rem" color="greyPrimary" fontWeight="normal">
                {MemberStore.isSignedIn ? 'ADD A NEW CARD' : 'CREDIT CARD'}
              </Label>
              <Flex ml={1} alignItems="center">
                {PaymentStore.availableCreditCardTypes.map(brand => (
                  <Flex key={brand} mr="0.5rem">
                    <Icon name={brand || 'card'} />
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )}
          <CreditCardForm
            display={showCreditCardForm ? 'block' : 'none'}
            onBrandChange={onBrandChange}
            onChange={onCreditCardChange}
          />
          {!showCreditCardForm && (
            <Button
              variant="link"
              my={1}
              onClick={() => {
                setPayWithCreditCardSelected(true);
                CartStore.payment.setPaymentMethod(undefined);
              }}
            >
              Add a new card
            </Button>
          )}
        </>
      )}
    </>
  );
};

const CardComponentObserved = observer(CardComponent);
export { CardComponentObserved as CardComponent };
